import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { 
  Shield, Users, Package, BarChart3, Facebook, 
  Ticket, HelpCircle, Settings, Save, RefreshCw,
  CheckCircle, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';

const PERMISSION_CONFIG = [
  { key: 'users', label: 'Gestion des utilisateurs', icon: Users, description: 'Voir et gérer les comptes utilisateurs' },
  { key: 'transactions', label: 'Gestion des transferts', icon: Package, description: 'Voir, modifier et réactiver les transferts de billets' },
  { key: 'analytics', label: 'Statistiques', icon: BarChart3, description: 'Accès au tableau de bord analytique' },
  { key: 'facebook', label: 'Groupes Facebook', icon: Facebook, description: 'Gérer les groupes Facebook affiliés' },
  { key: 'support', label: 'Support', icon: Ticket, description: 'Gérer les tickets de support' },
  { key: 'documentation', label: 'Documentation', icon: HelpCircle, description: 'Accès à la documentation plateforme' },
  { key: 'settings', label: 'Paramètres', icon: Settings, description: 'Accès aux paramètres de la plateforme' }
];

const AdminPermissionsManager = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [permissions, setPermissions] = useState({});

  useEffect(() => {
    loadAdmins();
  }, []);

  const loadAdmins = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/super-admin/users`);
      const adminUsers = response.data.filter(u => u.role === 'admin' || u.role === 'super_admin');
      setAdmins(adminUsers);
      
      // Load permissions for each admin
      const permsMap = {};
      for (const admin of adminUsers) {
        try {
          const permResponse = await axios.get(`${API}/super-admin/users/${admin.id}/permissions`);
          permsMap[admin.id] = permResponse.data.permissions;
        } catch (err) {
          // Use defaults if not found
          permsMap[admin.id] = {
            users: true,
            transactions: true,
            analytics: true,
            facebook: true,
            support: true,
            documentation: true,
            settings: false
          };
        }
      }
      setPermissions(permsMap);
    } catch (error) {
      toast.error('Erreur lors du chargement des administrateurs');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = (adminId, permKey, value) => {
    setPermissions(prev => ({
      ...prev,
      [adminId]: {
        ...prev[adminId],
        [permKey]: value
      }
    }));
  };

  const savePermissions = async (adminId) => {
    setSaving(prev => ({ ...prev, [adminId]: true }));
    try {
      await axios.put(
        `${API}/super-admin/users/${adminId}/permissions`,
        { permissions: permissions[adminId] }
      );
      toast.success('Permissions mises à jour avec succès');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde des permissions');
      console.error(error);
    } finally {
      setSaving(prev => ({ ...prev, [adminId]: false }));
    }
  };

  const setAllPermissions = (adminId, value) => {
    const newPerms = {};
    PERMISSION_CONFIG.forEach(p => {
      newPerms[p.key] = value;
    });
    setPermissions(prev => ({
      ...prev,
      [adminId]: newPerms
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-brand-600" />
        <span className="ml-3 text-gray-600">Chargement des administrateurs...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Gestion des permissions</h3>
          <p className="text-sm text-gray-500">Définissez les fonctionnalités accessibles pour chaque administrateur</p>
        </div>
        <Button variant="outline" onClick={loadAdmins}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {admins.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Shield className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">Aucun administrateur trouvé</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {admins.map(admin => (
            <Card key={admin.id} className="overflow-hidden">
              <CardHeader className="bg-gray-50 border-b">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${admin.role === 'super_admin' ? 'bg-purple-100' : 'bg-brand-100'}`}>
                      <Shield className={`h-5 w-5 ${admin.role === 'super_admin' ? 'text-purple-600' : 'text-brand-600'}`} />
                    </div>
                    <div>
                      <CardTitle className="text-base flex items-center gap-2">
                        {admin.email}
                        <Badge variant={admin.role === 'super_admin' ? 'default' : 'secondary'}>
                          {admin.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                        </Badge>
                      </CardTitle>
                      <CardDescription>
                        {admin.first_name} {admin.last_name}
                      </CardDescription>
                    </div>
                  </div>
                  
                  {admin.role !== 'super_admin' && (
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setAllPermissions(admin.id, true)}
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Tout activer
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setAllPermissions(admin.id, false)}
                      >
                        <AlertCircle className="h-4 w-4 mr-1" />
                        Tout désactiver
                      </Button>
                      <Button 
                        onClick={() => savePermissions(admin.id)}
                        disabled={saving[admin.id]}
                        className="bg-brand-600 hover:bg-brand-700"
                      >
                        {saving[admin.id] ? (
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4 mr-2" />
                        )}
                        Sauvegarder
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              
              <CardContent className="p-0">
                {admin.role === 'super_admin' ? (
                  <div className="p-6 bg-purple-50 text-center">
                    <Shield className="h-8 w-8 mx-auto text-purple-600 mb-2" />
                    <p className="text-purple-800 font-medium">Les super administrateurs ont accès à toutes les fonctionnalités</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 divide-y md:divide-y-0 md:divide-x">
                    {PERMISSION_CONFIG.map((perm, index) => {
                      const Icon = perm.icon;
                      const isEnabled = permissions[admin.id]?.[perm.key] ?? false;
                      
                      return (
                        <div 
                          key={perm.key}
                          className={`p-4 flex items-center justify-between hover:bg-gray-50 transition-colors ${
                            index >= 3 ? 'md:border-t' : ''
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${isEnabled ? 'bg-brand-100' : 'bg-gray-100'}`}>
                              <Icon className={`h-4 w-4 ${isEnabled ? 'text-brand-600' : 'text-gray-400'}`} />
                            </div>
                            <div>
                              <p className={`font-medium text-sm ${isEnabled ? 'text-gray-900' : 'text-gray-500'}`}>
                                {perm.label}
                              </p>
                              <p className="text-xs text-gray-400">{perm.description}</p>
                            </div>
                          </div>
                          <Switch
                            checked={isEnabled}
                            onCheckedChange={(checked) => handlePermissionChange(admin.id, perm.key, checked)}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminPermissionsManager;
